import { useState, useEffect } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Save, CheckCircle2, CalendarClock } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import { getNextRunPreview } from "@/lib/auto-invoice-preview";

const DAYS_OF_WEEK = [
  { value: "0", label: "Sunday" },
  { value: "1", label: "Monday" },
  { value: "2", label: "Tuesday" },
  { value: "3", label: "Wednesday" },
  { value: "4", label: "Thursday" },
  { value: "5", label: "Friday" },
  { value: "6", label: "Saturday" },
];

type Period = 'monthly' | 'biweekly' | 'quarterly' | 'weekly';

const usesDayOfWeek = (p: string) => p === 'weekly' || p === 'biweekly';
const usesDayOfMonth = (p: string) => p === 'monthly' || p === 'quarterly';

type Props = {
  orgSettings: any;
  timezone: string;
};

export default function AutoInvoiceSection({ orgSettings, timezone }: Props) {
  const { toast } = useToast();
  const qc = useQueryClient();
  const [saved, setSaved] = useState(false);

  const [enabled, setEnabled] = useState(false);
  const [period, setPeriod] = useState<string>("monthly");
  const [dayOfMonth, setDayOfMonth] = useState<number>(1);
  const [dayOfWeek, setDayOfWeek] = useState<number>(1); // Monday default

  useEffect(() => {
    if (orgSettings) {
      setEnabled(orgSettings.autoInvoiceEnabled ?? false);
      setPeriod(orgSettings.autoInvoicePeriod ?? "monthly");
      setDayOfMonth(orgSettings.autoInvoiceDayOfMonth ?? 1);
      setDayOfWeek(orgSettings.autoInvoiceDayOfWeek ?? 1);
    }
  }, [orgSettings]);

  const save = useMutation({
    mutationFn: () =>
      apiRequest("PATCH", "/api/admin/org-settings", {
        autoInvoiceEnabled: enabled,
        autoInvoicePeriod: period,
        autoInvoiceDayOfMonth: usesDayOfMonth(period) ? dayOfMonth : null,
        autoInvoiceDayOfWeek: usesDayOfWeek(period) ? dayOfWeek : null,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/admin/org-settings"] });
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
      toast({ title: "Auto-invoice settings saved" });
    },
    onError: () => toast({ title: "Failed to save", variant: "destructive" }),
  });

  const nextRun = enabled
    ? getNextRunPreview(period as Period, dayOfMonth, dayOfWeek, timezone)
    : null;

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center gap-2">
          <CalendarClock className="w-4 h-4 text-muted-foreground" />
          <CardTitle className="text-base">Auto-Invoice Generation</CardTitle>
        </div>
        <CardDescription>
          Automatically create draft invoices for owners at the end of each billing period. Drafts require manual review before sending.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center justify-between">
          <Label htmlFor="auto-invoice-toggle" className="text-sm">
            Enable auto-generation
          </Label>
          <Switch
            id="auto-invoice-toggle"
            checked={enabled}
            onCheckedChange={setEnabled}
            data-testid="switch-auto-invoice"
          />
        </div>

        {enabled && (
          <div className="space-y-3 pt-1">
            <div className="grid sm:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs">Billing period</Label>
                <Select value={period} onValueChange={setPeriod}>
                  <SelectTrigger className="h-8 text-sm" data-testid="select-period">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="weekly">Weekly</SelectItem>
                    <SelectItem value="biweekly">Every 2 weeks</SelectItem>
                    <SelectItem value="monthly">Monthly</SelectItem>
                    <SelectItem value="quarterly">Quarterly</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {usesDayOfWeek(period) && (
                <div className="space-y-1.5">
                  <Label className="text-xs">Generate on</Label>
                  <Select value={String(dayOfWeek)} onValueChange={v => setDayOfWeek(Number(v))}>
                    <SelectTrigger className="h-8 text-sm" data-testid="select-day-of-week">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {DAYS_OF_WEEK.map(d => (
                        <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {usesDayOfMonth(period) && (
                <div className="space-y-1.5">
                  <Label className="text-xs">Generate on day of period</Label>
                  <Input
                    type="number"
                    min={1}
                    max={28}
                    className="h-8 text-sm"
                    value={dayOfMonth}
                    onChange={e => setDayOfMonth(Math.min(28, Math.max(1, Number(e.target.value) || 1)))}
                    data-testid="input-day-of-month"
                  />
                  <p className="text-xs text-muted-foreground">Max 28 to avoid month-end issues</p>
                </div>
              )}
            </div>

            {nextRun && (
              <p className="text-xs text-muted-foreground">
                Next auto-generation run: <span className="font-medium text-foreground">{nextRun}</span>
              </p>
            )}
          </div>
        )}

        <div className="flex justify-end pt-1">
          <Button
            size="sm"
            onClick={() => save.mutate()}
            disabled={save.isPending}
            data-testid="button-save-auto-invoice"
          >
            {saved ? (
              <><CheckCircle2 className="w-4 h-4 mr-1" />Saved</>
            ) : save.isPending ? (
              "Saving…"
            ) : (
              <><Save className="w-4 h-4 mr-1" />Save</>
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
